import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import BrandMark from "../components/BrandMark";
import CodeVerifyForm from "../components/CodeVerifyForm";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const passwordChecks = (pw) => ({
  length: pw.length >= 8,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  number: /\d/.test(pw),
  symbol: /[^A-Za-z0-9]/.test(pw),
});

function PasswordRule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-[#279153]" : "text-[#5C7064]"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </li>
  );
}

export default function Register() {
  const { register, verifyRegister, resendCode } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("credentials");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pendingEmail, setPendingEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checks = passwordChecks(form.password);
  const passwordValid = Object.values(checks).every(Boolean);

  const submitCredentials = async (e) => {
    e.preventDefault();
    if (!passwordValid) {
      toast.error("Pick a stronger password");
      return;
    }
    setLoading(true);
    try {
      const r = await register(form.email, form.password, form.name);
      if (r?.codeRequired) {
        setPendingEmail(r.email);
        setStep("code");
        toast.success("Verification code sent");
      } else {
        nav("/orders");
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (code) => {
    await verifyRegister(pendingEmail, code);
    toast.success("Welcome to Grá");
    nav("/orders");
  };

  return (
    <div data-testid="register-page" className="pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md p-10 bg-[#09120C] border border-[#1A2B20]">
        <BrandMark variant="knot" className="h-12 w-12 mb-6 block" />

        {step === "credentials" ? (
          <form onSubmit={submitCredentials}>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Create account</div>
            <h1 className="font-serif-display text-3xl text-white mb-8">Join the studio</h1>

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Name</label>
            <input
              data-testid="register-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoComplete="name"
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-5"
            />

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Email</label>
            <input
              data-testid="register-email"
              type="email"
              required
              autoComplete="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-5"
            />

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Password</label>
            <input
              data-testid="register-password"
              type="password"
              required
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-4"
            />

            <ul data-testid="password-rules" className="text-xs space-y-1.5 mb-6">
              <PasswordRule ok={checks.length} label="At least 8 characters" />
              <PasswordRule ok={checks.upper} label="One uppercase letter" />
              <PasswordRule ok={checks.lower} label="One lowercase letter" />
              <PasswordRule ok={checks.number} label="One number" />
              <PasswordRule ok={checks.symbol} label="One symbol (!@#$ etc.)" />
            </ul>

            <button
              type="submit"
              data-testid="register-submit"
              disabled={loading || !passwordValid}
              className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              {loading ? "…" : "Continue"}
            </button>

            <p className="mt-6 text-sm text-[#95A89C] text-center">
              Already have an account?{" "}
              <Link to="/login" data-testid="register-go-login" className="text-[#279153] hover:underline">Sign in</Link>
            </p>
          </form>
        ) : (
          <CodeVerifyForm
            email={pendingEmail}
            purpose="register"
            submitLabel="Verify & create account"
            testidPrefix="register-code"
            onSubmit={verifyCode}
            onResend={() => resendCode(pendingEmail, "register")}
            onBack={() => setStep("credentials")}
          />
        )}
      </div>
    </div>
  );
}
